import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AdminLayout from "../../../components/AdminLayout";
import { Cabecalho, Pagina } from "../../../components/AdminLayout/Pagina";
import { avisarPedidosAlterados, usePendentesPorTipo } from "../../../components/AdminLayout/usePendentes";
import Abas from "../../../components/Abas";
import Paginacao from "../../../components/Paginacao";
import TabelaAdmin from "../../../components/TabelaAdmin";
import Esqueleto, { Recarregando } from "../../../components/Esqueleto";
import CircularImage from "../../../components/CircularImage";
import perfil_icon from "../../../../assets/perfil.png";
import SolicitacaoProfissional from "../../../../models/SolicitacaoProfissional";
import SolicitacaoApoiador from "../../../../models/SolicitacaoApoiador";
import SolicitacaoPatrocinador from "../../../../models/SolicitacaoPatrocinador";
import { linkValido } from "../../../../utils/links";
import { caminhoCadastrarBanner } from "../../../../utils/pedidoBanner";
import { mensagemDeErro, respostaOk } from "../../../../utils/resposta";
import DecisaoDialogo from "./DecisaoDialogo";
import { Corpo } from "./styled";

const POR_PAGINA_INICIAL = 20;
const MIN_MOTIVO = 3;
const MENSAGEM_LONGA = 140;

// Cada aba de situação é um filtro; a aba vai na URL (?aba=aprovados).
const ABAS = [
  { id: "pendentes", rotulo: "Pendentes", status: "Pendente" },
  { id: "aprovados", rotulo: "Aprovados", status: "Aprovada" },
  { id: "recusados", rotulo: "Recusados", status: "Recusada" },
  { id: "todos", rotulo: "Todos", status: "" },
];

// Os três tipos de pedido (?tipo=apoiador|patrocinador). O desenho é o mesmo; muda o model, as
// colunas e o que a aprovação faz.
const TIPOS = {
  profissional: {
    id: "profissional", rotulo: "Perfil Profissional", model: SolicitacaoProfissional,
    tabela: "Pedidos de perfil Profissional",
    aprovado: (nome) => `${nome} agora é Profissional.`,
    recusado: (nome) => `Pedido de ${nome} recusado.`,
    aviso: (nome) => `Ao aprovar, ${nome} passa a ter o perfil Profissional na hora: registrar áreas no site e criar subgrupos.`,
    vazioPendente: "Nenhum pedido de perfil aguardando resposta.",
  },
  apoiador: {
    id: "apoiador", rotulo: "Apoiadores", model: SolicitacaoApoiador,
    tabela: "Pedidos para ser apoiador",
    aprovado: (nome) => `${nome} agora aparece como apoiador do projeto.`,
    recusado: (nome) => `Pedido de apoio de ${nome} recusado.`,
    aviso: (nome) => `Ao aprovar, ${nome} passa a aparecer como apoiador do projeto no app, com o nome, o estado e a foto do próprio perfil.`,
    vazioPendente: "Nenhum pedido de apoio aguardando resposta.",
  },
  // Patrocinador: aprovar só MARCA o pedido; o banner exige imagem e link, que o pedido
  // não traz, e a combinação comercial é fora do sistema. O administrador cadastra o
  // banner em Conteúdo > Banners (o atalho "Cadastrar banner" leva os dados do pedido).
  patrocinador: {
    id: "patrocinador", rotulo: "Patrocinadores", model: SolicitacaoPatrocinador,
    tabela: "Pedidos para ser patrocinador",
    aprovado: (nome) => `Pedido de patrocínio de ${nome} aprovado.`,
    recusado: (nome) => `Pedido de patrocínio de ${nome} recusado.`,
    aviso: (nome) => `Aprovar marca o pedido de ${nome} como aprovado; o banner é cadastrado em Conteúdo > Banners. Entre em contato pelo e-mail ou celular do perfil para combinar os detalhes.`,
    vazioPendente: "Nenhum pedido de patrocínio aguardando resposta.",
  },
};

const data = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

// Texto longo com "ver mais" (a declaração/mensagem da pessoa pode ter centenas de
// caracteres e não cabe numa linha de lista).
function TextoLongo({ id, texto, expandidas, alternar }) {
  const longa = (texto || "").length > MENSAGEM_LONGA;
  const aberta = expandidas.has(id);
  return (
    <div>
      <p className={`mensagem${longa && !aberta ? " curta" : ""}`}>{texto}</p>
      {longa &&
        <button type="button" className="ver-mais" aria-expanded={aberta} onClick={() => alternar(id)}>
          {aberta ? "ver menos" : "ver mais"}
        </button>
      }
    </div>
  );
}

const Datas = ({ p }) => (
  <div className="datas">
    <div><b>Pedido</b> {data(p.createdAt)}</div>
    {p.status !== "Pendente" && p.decidido_em && <div><b>Decisão</b> {data(p.decidido_em)}</div>}
  </div>
);

// Pedido de apoio/patrocinio aprovado e depois DESABILITADO pelo administrador: o selo
// mostra a situacao real (o pedido continua "Aprovado" no historico). `apoio_ativo` so vem
// nos pedidos de apoiador e patrocinador; no de perfil Profissional e undefined.
const desabilitado = (p) => p.status === "Aprovada" && p.apoio_ativo === false;

const Situacao = ({ p }) => (
  <div>
    <span className={`selo ${p.status}`}>{p.status}</span>
    {desabilitado(p) && <span className="selo Desabilitado" title="O apoio foi desabilitado e não aparece no site nem no app">Desabilitado</span>}
    {p.status !== "Pendente" && p.resposta && <p className="resposta">“{p.resposta}”</p>}
  </div>
);

const Acoes = ({ p, abrirDecisao, atalhoBanner = false }) => {
  if (p.status === "Pendente") {
    return (
      <div className="acoes">
        <button type="button" onClick={() => abrirDecisao(p, "aprovar")}>Aprovar</button>
        <button type="button" className="secundario" onClick={() => abrirDecisao(p, "recusar")}>Recusar</button>
      </div>
    );
  }
  // Apoiador e patrocinador aprovados: o administrador pode desabilitar o apoio a qualquer
  // momento e habilitar de volta. (`apoio_ativo` ausente = pedido de perfil Profissional.)
  if (p.status === "Aprovada" && typeof p.apoio_ativo === "boolean") {
    return (
      <div className="acoes">
        {/* Patrocinio: o proximo passo e cadastrar o banner (so enquanto habilitado). */}
        {atalhoBanner && p.apoio_ativo && <Link className="atalho" to={caminhoCadastrarBanner(p)}>Cadastrar banner</Link>}
        {p.apoio_ativo
          ? <button type="button" className="secundario" onClick={() => abrirDecisao(p, "desabilitar")}>Desabilitar</button>
          : <button type="button" onClick={() => abrirDecisao(p, "habilitar")}>Habilitar</button>}
      </div>
    );
  }
  return <span className="sem-acao">Decidido</span>;
};

// Colunas de cada tipo. `ctx` traz o que a linha precisa do estado da página.
const COLUNAS = {
  profissional: ({ expandidas, alternarMensagem, abrirDecisao }) => [
    {
      id: "solicitante", rotulo: "Solicitante", largura: "minmax(150px, 1.4fr)",
      celula: (p) => {
        const s = p.usuario || {};
        return (
          <div>
            <div className="nome">{s.nome || "Usuário removido"}</div>
            <div className="meta">{[s.email, s.celular].filter(Boolean).join(" · ")}</div>
            <div className="meta">{[s.cidade, s.estado].filter(Boolean).join(" / ") || "Local não informado"}</div>
          </div>
        );
      },
    },
    {
      id: "atuacao", rotulo: "Atuação", largura: "minmax(120px, 1.1fr)",
      celula: (p) => (
        <div>
          <div className="atuacao">{p.tipo_atuacao}</div>
          {p.organizacao && <div className="meta">{p.organizacao}</div>}
          {p.area_estimada_ha != null && <div className="meta">{Number(p.area_estimada_ha).toLocaleString("pt-BR")} ha estimados</div>}
          {p.usuario?.tipo && <div className="meta">Perfil atual: {p.usuario.tipo}</div>}
        </div>
      ),
    },
    {
      id: "mensagem", rotulo: "Mensagem", largura: "minmax(150px, 2fr)",
      celula: (p) => <TextoLongo id={p.id} texto={p.mensagem} expandidas={expandidas} alternar={alternarMensagem} />,
    },
    { id: "datas", rotulo: "Datas", largura: "96px", celula: (p) => <Datas p={p} /> },
    { id: "status", rotulo: "Situação", largura: "minmax(100px, 0.9fr)", celula: (p) => <Situacao p={p} /> },
    { id: "acoes", rotulo: "Ações", largura: "minmax(130px, auto)", direita: true, celula: (p) => <Acoes p={p} abrirDecisao={abrirDecisao} /> },
  ],

  // Apoiador: nome, estado e foto são os do PERFIL da pessoa (é o que vai aparecer
  // no app), então a linha mostra exatamente isso.
  apoiador: ({ expandidas, alternarMensagem, abrirDecisao }) => [
    {
      id: "solicitante", rotulo: "Solicitante", largura: "minmax(150px, 1.6fr)",
      celula: (p) => {
        const s = p.usuario || {};
        return (
          <div className="solicitante-linha">
            <CircularImage src={s.foto_perfil?.path || perfil_icon} alt="" className="miniatura-perfil" />
            <div>
              <div className="nome">{s.nome || "Usuário removido"}</div>
              <div className="meta">{[s.email, s.celular].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
        );
      },
    },
    {
      id: "local", rotulo: "Local", largura: "minmax(100px, 1fr)",
      celula: (p) => <span>{[p.usuario?.cidade, p.usuario?.estado].filter(Boolean).join(" / ") || "Não informado"}</span>,
    },
    {
      id: "declaracao", rotulo: "Declaração de apoio", largura: "minmax(150px, 2fr)",
      celula: (p) => <TextoLongo id={p.id} texto={p.declaracao} expandidas={expandidas} alternar={alternarMensagem} />,
    },
    {
      id: "link", rotulo: "Link", largura: "minmax(100px, 1fr)",
      // Só http/https vira link clicável (o backend já recusa o resto; aqui é a segunda linha de defesa).
      celula: (p) => (p.link && linkValido(p.link)
        ? <a className="link" href={p.link} target="_blank" rel="noopener noreferrer">{p.link}</a>
        : <span className="meta">Sem link</span>),
    },
    { id: "datas", rotulo: "Datas", largura: "96px", celula: (p) => <Datas p={p} /> },
    { id: "status", rotulo: "Situação", largura: "minmax(100px, 0.9fr)", celula: (p) => <Situacao p={p} /> },
    { id: "acoes", rotulo: "Ações", largura: "minmax(130px, auto)", direita: true, celula: (p) => <Acoes p={p} abrirDecisao={abrirDecisao} /> },
  ],

  // Patrocinador: o administrador precisa do CONTATO (e-mail e celular do perfil) para
  // falar com a empresa, e da empresa/site para cadastrar o banner. Seis colunas (empresa
  // e site na mesma; as datas dentro de Situação): com oito, a largura mínima passava da
  // página e a lista caía no layout empilhado mesmo em tela larga.
  patrocinador: ({ expandidas, alternarMensagem, abrirDecisao }) => [
    {
      id: "solicitante", rotulo: "Solicitante", largura: "minmax(140px, 1.3fr)",
      celula: (p) => {
        const s = p.usuario || {};
        return (
          <div className="solicitante-linha">
            <CircularImage src={s.foto_perfil?.path || perfil_icon} alt="" className="miniatura-perfil" />
            <div>
              <div className="nome">{s.nome || "Usuário removido"}</div>
              <div className="meta">{[s.cidade, s.estado].filter(Boolean).join(" / ") || "Local não informado"}</div>
            </div>
          </div>
        );
      },
    },
    {
      id: "contato", rotulo: "Contato", largura: "minmax(150px, 1.2fr)",
      celula: (p) => {
        const s = p.usuario || {};
        return (
          <div>
            {s.email ? <a className="link" href={`mailto:${s.email}`}>{s.email}</a> : <span className="meta">Sem e-mail</span>}
            <div className="meta">{s.celular ? <a className="link" href={`tel:${s.celular.replace(/[^\d+]/g, "")}`}>{s.celular}</a> : "Celular não informado"}</div>
          </div>
        );
      },
    },
    {
      id: "empresa", rotulo: "Empresa", largura: "minmax(120px, 1.3fr)",
      celula: (p) => (
        <div>
          <div className="nome">{p.empresa}</div>
          {p.link && linkValido(p.link)
            ? <a className="link" href={p.link} target="_blank" rel="noopener noreferrer">{p.link}</a>
            : <span className="meta">Sem link</span>}
        </div>
      ),
    },
    {
      id: "mensagem", rotulo: "Mensagem", largura: "minmax(140px, 2fr)",
      celula: (p) => <TextoLongo id={p.id} texto={p.mensagem} expandidas={expandidas} alternar={alternarMensagem} />,
    },
    {
      id: "status", rotulo: "Situação", largura: "minmax(100px, 0.9fr)",
      celula: (p) => (
        <div>
          <Situacao p={p} />
          <Datas p={p} />
        </div>
      ),
    },
    { id: "acoes", rotulo: "Ações", largura: "minmax(140px, auto)", direita: true, celula: (p) => <Acoes p={p} abrirDecisao={abrirDecisao} atalhoBanner /> },
  ],
};

/**
 * Fila de pedidos do painel, em lista de largura total, com três tipos:
 *  - perfil Profissional (aprovar PROMOVE a pessoa);
 *  - apoiador (aprovar CRIA o apoiador, com os dados do perfil da pessoa);
 *  - patrocinador (aprovar MARCA o pedido; o banner é cadastrado em Conteúdo).
 * O backend faz a aprovação numa transação, então não há segundo passo no painel.
 * Recusar exige o motivo: sem ele a pessoa não sabe o que corrigir nem se vale pedir
 * de novo.
 */
export default function Pedidos() {
  const [params, setParams] = useSearchParams();
  const tipo = TIPOS[params.get("tipo")] || TIPOS.profissional;
  const abaAtual = ABAS.find((a) => a.id === params.get("aba")) || ABAS[0];
  // Os dois parâmetros andam juntos: trocar um mantém o outro.
  const irPara = (novoTipo, novaAba) => setParams({ tipo: novoTipo, aba: novaAba }, { replace: true });

  const pendentes = usePendentesPorTipo(true);

  const [page, setPage] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_INICIAL);
  const [pedidos, setPedidos] = useState({ count: 0, rows: [] });
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [feedback, setFeedback] = useState("");
  // Depois de aprovar um pedido de patrocínio: o pedido, para o atalho "Cadastrar banner".
  const [aprovadoPatrocinio, setAprovadoPatrocinio] = useState(null);
  const [expandidas, setExpandidas] = useState(() => new Set());
  // { pedido, acao: 'aprovar' | 'recusar' } — o pedido com o diálogo de decisão aberto
  const [decisao, setDecisao] = useState(null);
  const [resposta, setResposta] = useState("");
  const [erroDecisao, setErroDecisao] = useState("");
  const requisicao = useRef(0);

  const buscar = useCallback(async () => {
    const atual = ++requisicao.current;
    setLoading(true);
    const resultado = await tipo.model.findAll(page, porPagina, abaAtual.status);
    if (atual !== requisicao.current) return;

    if (respostaOk(resultado)) {
      const { count, rows } = resultado.data;
      // Página que deixou de existir (decidiu o último pedido dela): última existente.
      if (rows.length === 0 && count > 0 && page > 1) {
        setPage(Math.ceil(count / porPagina));
        return;
      }
      setErro("");
      setPedidos(resultado.data);
    } else {
      setErro(mensagemDeErro(resultado));
    }
    setLoading(false);
  }, [tipo, page, porPagina, abaAtual.status]);

  useEffect(() => { buscar(); }, [buscar]);

  // Trocou de tipo ou de aba: recomeça da primeira página, sem o conteúdo anterior.
  useEffect(() => {
    setPage(1);
    setPedidos({ count: 0, rows: [] });
    setErro("");
    setFeedback("");
    setAprovadoPatrocinio(null);
    setDecisao(null);
  }, [tipo.id, abaAtual.id]);

  function abrirDecisao(pedido, acao) {
    setDecisao({ pedido, acao });
    setResposta("");
    setErroDecisao("");
  }

  async function alternarApoio() {
    const { pedido, acao } = decisao;
    const ativando = acao === "habilitar";
    setEnviando(true);
    const resultado = ativando ? await tipo.model.habilitar(pedido.id) : await tipo.model.desabilitar(pedido.id);
    setEnviando(false);
    if (!respostaOk(resultado)) { setErroDecisao(mensagemDeErro(resultado)); return; }

    const nome = pedido.usuario?.nome || "A pessoa";
    setDecisao(null);
    setAprovadoPatrocinio(null);
    setFeedback(ativando
      ? `O apoio de ${nome} foi habilitado e voltou a aparecer.`
      : `O apoio de ${nome} foi desabilitado e não aparece mais no site nem no app.`);
    await buscar();
  }

  async function confirmar() {
    const { pedido, acao } = decisao;
    if (acao === "desabilitar" || acao === "habilitar") return alternarApoio();
    const recusando = acao === "recusar";

    if (recusando && resposta.trim().length < MIN_MOTIVO) {
      setErroDecisao(`Informe o motivo da recusa (mínimo de ${MIN_MOTIVO} caracteres).`);
      return;
    }

    setEnviando(true);
    const resultado = recusando
      ? await tipo.model.recusar(pedido.id, resposta.trim())
      : await tipo.model.aprovar(pedido.id, resposta.trim());
    setEnviando(false);

    if (!respostaOk(resultado)) {
      // 409 (já decidido por outro administrador) cai aqui com a mensagem do backend.
      setErroDecisao(mensagemDeErro(resultado));
      return;
    }

    const nome = pedido.usuario?.nome || "A pessoa";
    setDecisao(null);
    setFeedback(recusando ? tipo.recusado(nome) : tipo.aprovado(nome));
    setAprovadoPatrocinio(!recusando && tipo.id === "patrocinador" ? pedido : null);
    setPage(1);
    await buscar();
    avisarPedidosAlterados();
  }

  const alternarMensagem = (id) => setExpandidas((atual) => {
    const novo = new Set(atual);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    return novo;
  });

  const colunas = COLUNAS[tipo.id]({ expandidas, alternarMensagem, abrirDecisao });
  const abasTipo = [
    { id: "profissional", rotulo: TIPOS.profissional.rotulo, selo: pendentes.profissional },
    { id: "apoiador", rotulo: TIPOS.apoiador.rotulo, selo: pendentes.apoiador },
    { id: "patrocinador", rotulo: TIPOS.patrocinador.rotulo, selo: pendentes.patrocinador },
  ];

  return (
    <AdminLayout>
      <Pagina>
        <Cabecalho titulo="Pedidos" descricao="Pessoas que pediram o perfil Profissional (registrar áreas e criar subgrupos), para aparecer como apoiadoras do projeto ou para patrocinar o projeto com um banner." />

        <Abas abas={abasTipo} ativa={tipo.id} onChange={(id) => irPara(id, abaAtual.id)} idBase="pedidos-tipo" rotulo="Tipo de pedido" />

        <div role="tabpanel" id={`pedidos-tipo-painel-${tipo.id}`} aria-labelledby={`pedidos-tipo-${tipo.id}`} style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", minWidth: 0 }}>
          <Abas abas={ABAS} ativa={abaAtual.id} onChange={(id) => irPara(tipo.id, id)} idBase="pedidos" rotulo="Situação dos pedidos" />

          <Corpo role="tabpanel" id={`pedidos-painel-${abaAtual.id}`} aria-labelledby={`pedidos-${abaAtual.id}`}>
            {feedback &&
              <span className="feedback" role="status">
                {feedback}
                {aprovadoPatrocinio &&
                  <> <Link className="atalho-feedback" to={caminhoCadastrarBanner(aprovadoPatrocinio)}>Cadastrar banner agora</Link></>
                }
              </span>
            }
            {erro && <span className="erro-lista" role="alert">{erro}</span>}

            {!loading && !erro && pedidos.rows.length === 0 &&
              <span className="vazio">
                {abaAtual.status === "Pendente" ? tipo.vazioPendente : "Nenhum pedido nesta situação."}
              </span>
            }

            {loading && pedidos.rows.length === 0 && <Esqueleto variante="linha" quantidade={5} rotulo="Carregando pedidos" />}
            <Recarregando $ativo={loading && pedidos.rows.length > 0} aria-busy={loading && pedidos.rows.length > 0}>
              <TabelaAdmin
                rotulo={tipo.tabela}
                colunas={colunas}
                linhas={pedidos.rows}
                chave={(p) => `pedido-${tipo.id}-${p.id}`}
                larguraMinima={tipo.id === "apoiador" ? 900 : 840}
              />
            </Recarregando>

            {!(loading && pedidos.rows.length === 0) &&
            <Paginacao
              pagina={page} total={pedidos.count} porPagina={porPagina} itens="pedidos"
              onMudarPagina={setPage}
              onMudarPorPagina={(n) => { setPorPagina(n); setPage(1); }}
            />
            }
          </Corpo>
        </div>

        {decisao &&
          <DecisaoDialogo
            minimoMotivo={MIN_MOTIVO}
            pedido={decisao.pedido} acao={decisao.acao}
            avisoAprovacao={tipo.aviso(decisao.pedido.usuario?.nome || "a pessoa")}
            resposta={resposta} setResposta={(v) => { setResposta(v); setErroDecisao(""); }}
            erro={erroDecisao} enviando={enviando}
            onConfirmar={confirmar} onCancelar={() => setDecisao(null)}
          />
        }
      </Pagina>
    </AdminLayout>
  );
}
